import {useCallback, useEffect} from "react";
import {batch, useComputed, useSignal} from "@preact/signals-react";
import {useAppToast} from "@ui-seeker/app";
import {postSubmit, isEmpty} from "@ui-seeker/utils";

import {WEBBACK_V2_API} from "@constants";
import {isLoadingCommonFilters, commonFiltersValue, isSystemBusy} from "@signals";
import {ORDER_WIDGET_BY} from "../@contants";
import {PriceEvolutionCurrentTabId, PriceEvolutionCurrentTabLabel} from "../@signals";


export default function useRetailerPriceEvolutionWidgetPricing() {
	const { showToast } = useAppToast()
	const localLoading = useSignal(false)
	const localPrevLoading = useSignal(false)
	const localLoadingMinMax = useSignal(false)
	const data = useSignal({})
	const PrevData = useSignal({})
	const dataMinMax = useSignal({})
	const isPercentage = useSignal(false)
	const isSplitBySku = useSignal(true)
	const showListPrice = useSignal(false)
	const orderBy = useSignal(ORDER_WIDGET_BY.PRICE)

	const isLoading = useComputed(() =>
		isLoadingCommonFilters.value || localLoading.value || localLoadingMinMax.value || localPrevLoading.value
	)
	const selectedLabel = useComputed(() => isLoading.value ? null : PriceEvolutionCurrentTabLabel.value)
	const currentData = useComputed(() => {
		if (isLoading.value)
			return {}
		const current = data.value[PriceEvolutionCurrentTabId.value] || {}
		const prev = PrevData.value[PriceEvolutionCurrentTabId.value] || {}
		if (isEmpty(current))
			return {}
		if (isEmpty(prev))
			return current

		const __setData = (d, dataType) => {
			const childID = isSplitBySku.value ? d.siteID : d.skuID
			const prevRow = (prev[dataType] || {})[childID] || {}

			return {
				...d,
				actualPrevKPI: prevRow?.actualKPI,
				listPrevKPI: prevRow?.listKPI,
			}
		}

		return {
			avgWidgets: current.avgWidgets.map(d => __setData(d, 'avgWidgets')) || []
		}
	})
	const currentDataMinMax = useComputed(() => isLoading.value ? [] : (dataMinMax.value[PriceEvolutionCurrentTabId.value] || {}))

	useEffect(() => {
		if (isLoading.value && isLoadingCommonFilters.value) {
			data.value = {}
			PrevData.value = {}
			dataMinMax.value = {}
			handleSubmit()
		}
	}, [isLoading.value]);

	useEffect(() => {
		if (PriceEvolutionCurrentTabId.value && !isLoading.value && !(PriceEvolutionCurrentTabId.value in data.value))
			handleSubmit(true)
	}, [PriceEvolutionCurrentTabId.value]);

	const handleSubmit = (useTab=false) => {
		const filters = {...commonFiltersValue.value}
		if (useTab) {
			if (isSplitBySku.value) {
				filters.sku = [PriceEvolutionCurrentTabId.value]
			} else {
				filters.retailer = [PriceEvolutionCurrentTabId.value]
			}
		}

		if (!localLoading.value)
			localLoading.value = true
		postSubmit(`${WEBBACK_V2_API}/pricing/price-evolution/widget-pricing`, filters).then(res => batch(() => {
			localLoading.value = false
			data.value = {...data.value, ...res.widget}
			isPercentage.value = res.isPercentage
			isSplitBySku.value = res.isSplitBySku
		})).catch(() => batch(() => {
			localLoading.value = false
			isSystemBusy.value = true
		}))

		if (!localPrevLoading.value)
			localPrevLoading.value = true
		postSubmit(`${WEBBACK_V2_API}/pricing/price-evolution/widget-pricing?isPrevious=true`, filters).then(res => batch(() => {
			localPrevLoading.value = false
			PrevData.value = {...PrevData.value, ...res.widget}
		})).catch(() => batch(() => {
			localPrevLoading.value = false
			isSystemBusy.value = true
		}))

		if (!localLoadingMinMax.value)
			localLoadingMinMax.value = true
		postSubmit(`${WEBBACK_V2_API}/pricing/price-evolution/widget-min-max`, filters).then(res => batch(() => {
			localLoadingMinMax.value = false
			dataMinMax.value = {...dataMinMax.value, ...res.widgetMinMax}
		})).catch(() => batch(() => {
			localLoadingMinMax.value = false
			isSystemBusy.value = true
		}))
	}

	const handleShowListPrice = useCallback(newValue => {
		showListPrice.value = newValue
	}, [])

	const handleOrder = useCallback(newValue => {
		orderBy.value = newValue
	}, [])

	return {
		isLoading: isLoading.value,
		allData: data.value,
		isPercentage: isPercentage.value,
		isSplitBySku: isSplitBySku.value,
		showListPrice: showListPrice.value,
		orderBy: orderBy.value,
		currentData: currentData.value,
		currentDataMinMax: currentDataMinMax.value,
		selectedLabel: selectedLabel.value,

		handleShowListPrice,
		handleOrder
	}
}